import React from 'react';
import { LayerType } from '../../../types';
import { useMapCanvas } from '../contexts/MapCanvasContext';
import { useMapEditor } from '../contexts/MapEditorContext';
import { DraggableShape } from '../shapes/DraggableShape/DraggableShape';
import { ShapeProvider } from '../shapes/ShapeContext/ShapeProvider';

export type UseGetEditorShapesByTypeReturn = (
  type: LayerType
) => React.ReactNode;

export const useGetEditorShapesByType = (): UseGetEditorShapesByTypeReturn => {
  const {
    shapes,
    selectedIds,
    snapToGrid,
    showSnapLines,
    activeTool: tool,
    moveShape: onMoveShape,
    deleteShape: onDeleteShape,
    locateShape: onLocateShape,
    updateShape: onShapeUpdate,
    setSelectedIds: onSelectionChange,
    shouldPreventDeselect,
    setContextMenuShape,
    setHighlightedShape,
    highlightedShape,
    setNodeStyleToolTarget,
    nodeStyleToolTarget,
    copyNodeStyleToNodeTarget,
    duplicateShape
  } = useMapEditor();
  const { gridSize, isAltPressed, shapesInSelection } = useMapCanvas();

  const shapeSelectionIndex = selectedIds.reduce(
    (acc, shapeId) => {
      const shape = shapes.find((s) => s.id === shapeId);
      if (shape?.type === 'spot') {
        acc[shapeId] = Object.keys(acc).length;
      }
      return acc;
    },
    {} as Record<string, number>
  );

  return (type: LayerType) => {
    return shapes
      .filter((shape) => shape.layerType === type)
      .map((shape) => (
        <ShapeProvider
          key={shape.id}
          shape={shape}
          isSelected={selectedIds.includes(shape.id)}
          isInSelection={shapesInSelection.includes(shape.id)}
          onSelect={(e) => {
            if (shape.type === 'spot') {
              if (tool === 'node-style') {
                if (
                  nodeStyleToolTarget &&
                  nodeStyleToolTarget.id !== shape.id
                ) {
                  copyNodeStyleToNodeTarget(nodeStyleToolTarget.id, shape.id);
                } else {
                  setNodeStyleToolTarget(shape);
                }
                return;
              }
            }
            if (tool === 'select' && !e.evt.altKey) {
              e.cancelBubble = true;
              shouldPreventDeselect.current = true;

              const isMultiSelect = e.evt.shiftKey;

              if (isMultiSelect) {
                const newSelectedIds = selectedIds.includes(shape.id)
                  ? selectedIds.filter((selectedId) => selectedId !== shape.id)
                  : [...selectedIds, shape.id];
                onSelectionChange(newSelectedIds);
              } else {
                onSelectionChange([shape.id]);
              }
            }
          }}
          onUpdate={(newProps) => onShapeUpdate(shape.id, newProps)}
          snapToGrid={snapToGrid}
          gridSize={gridSize}
          otherShapes={shapes}
          showSnapLines={showSnapLines}
          disabled={tool !== 'select' || isAltPressed}
          isMultiSelected={selectedIds.length > 1}
          onDelete={() => onDeleteShape(shape.id)}
          onLocate={() => onLocateShape(shape.id)}
          onMoveUp={() => onMoveShape(shape.id, 'up')}
          onMoveDown={() => onMoveShape(shape.id, 'down')}
          selectIndex={
            selectedIds.length > 1 ? shapeSelectionIndex[shape.id] : undefined
          }
        >
          <DraggableShape
            key={shape.id}
            setContextMenuShape={setContextMenuShape}
            setHighlightedShape={setHighlightedShape}
            highlightedShape={highlightedShape}
            onStartDragDuplicate={() => {
              duplicateShape(shape.id, {
                offset: { x: 0, y: 0 },
                selectDuplicates: false,
                switchResourceId: true,
                switchIndex: true
              });
            }}
          />
        </ShapeProvider>
      ));
  };
};
